import type { WorkingDayKey } from '../WorkingDays/WorkingDays.types'
import {
  WORKING_DAY_KEYS,
  WORKING_DAY_TITLES,
} from '../WorkingDays/WorkingDays.types'
import type {
  DayTimeParts,
  DayTimePeriod,
  DayTimeSelectorValue,
} from './DayTimeSelector.types'
import { EMPTY_DAY_TIME_SELECTOR_VALUE } from './DayTimeSelector.types'

const SHORT_DAY_LABELS: Record<WorkingDayKey, string> = {
  mon: 'Mon',
  tue: 'Tue',
  wed: 'Wed',
  thu: 'Thu',
  fri: 'Fri',
  sat: 'Sat',
  sun: 'Sun',
}

const SHORT_DAY_TO_KEY: Record<string, WorkingDayKey> = {
  Mon: 'mon',
  Tue: 'tue',
  Wed: 'wed',
  Thu: 'thu',
  Fri: 'fri',
  Sat: 'sat',
  Sun: 'sun',
}

export const isDayTimePartsComplete = (
  parts: DayTimeParts | null | undefined,
): parts is DayTimeParts =>
  Boolean(parts?.hour && parts?.minute && parts?.period)

export const formatDayTimeParts = (parts: DayTimeParts): string =>
  `${parts.hour}:${parts.minute} ${parts.period}`

export const formatDayRanges = (days: WorkingDayKey[]): string => {
  if (days.length === 0) {
    return ''
  }

  const indices = WORKING_DAY_KEYS.map((key, index) =>
    days.includes(key) ? index : -1,
  ).filter((index) => index >= 0)

  const ranges: string[] = []
  let start = indices[0]
  let end = start

  for (let i = 1; i <= indices.length; i += 1) {
    if (indices[i] === end + 1) {
      end = indices[i]
      continue
    }

    const startKey = WORKING_DAY_KEYS[start]
    const endKey = WORKING_DAY_KEYS[end]
    ranges.push(
      start === end
        ? SHORT_DAY_LABELS[startKey]
        : `${SHORT_DAY_LABELS[startKey]}-${SHORT_DAY_LABELS[endKey]}`,
    )
    start = indices[i]
    end = start
  }

  return ranges.join(',')
}

export const formatDayTimeSelectorValue = (
  value: DayTimeSelectorValue,
): string => {
  const dayText = formatDayRanges(value.days)
  const start = value.start
  const end = value.end
  const hasStart = isDayTimePartsComplete(start)
  const hasEnd = isDayTimePartsComplete(end)

  if (!dayText && !hasStart && !hasEnd) {
    return ''
  }

  if (hasStart && hasEnd) {
    return `${dayText || 'Any day'} (${formatDayTimeParts(start)} - ${formatDayTimeParts(end)})`
  }

  if (dayText) {
    return dayText
  }

  if (hasStart) {
    return formatDayTimeParts(start)
  }

  if (hasEnd) {
    return formatDayTimeParts(end)
  }

  return ''
}

export const truncateDayTimeSummary = (
  summary: string,
  maxLength = 36,
): string =>
  summary.length > maxLength ? `${summary.slice(0, maxLength)}...` : summary

const parseTimeParts = (raw: string): DayTimeParts | null => {
  const match = raw.trim().match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i)
  if (!match) {
    return null
  }

  return {
    hour: String(Number.parseInt(match[1], 10)),
    minute: match[2],
    period: match[3].toUpperCase() as DayTimePeriod,
  }
}

const parseDayRanges = (raw: string): WorkingDayKey[] => {
  const days: WorkingDayKey[] = []
  raw
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean)
    .forEach((range) => {
      const [startLabel, endLabel] = range.split('-')
      const startKey = SHORT_DAY_TO_KEY[startLabel]
      const endKey = endLabel ? SHORT_DAY_TO_KEY[endLabel] : startKey
      if (!startKey || !endKey) {
        return
      }

      const startIndex = WORKING_DAY_KEYS.indexOf(startKey)
      const endIndex = WORKING_DAY_KEYS.indexOf(endKey)
      for (let i = startIndex; i <= endIndex; i += 1) {
        days.push(WORKING_DAY_KEYS[i])
      }
    })

  return Array.from(new Set(days))
}

/** Parse legacy Add Patient string: `Mon-Fri (9:00 AM - 5:00 PM)`. */
export const parseDayTimeSelectorValue = (
  raw: string | null | undefined,
): DayTimeSelectorValue => {
  if (!raw?.trim()) {
    return { ...EMPTY_DAY_TIME_SELECTOR_VALUE, days: [] }
  }

  const openParen = raw.indexOf('(')
  const dayPart = (openParen >= 0 ? raw.slice(0, openParen) : raw).trim()
  const timePart =
    openParen >= 0
      ? raw
          .slice(openParen + 1)
          .replace(/\)\s*$/, '')
          .trim()
      : ''

  const [startRaw, endRaw] = timePart.split(/\s*-\s*/)
  return {
    days: parseDayRanges(dayPart),
    start: startRaw ? parseTimeParts(startRaw) : null,
    end: endRaw ? parseTimeParts(endRaw) : null,
  }
}

export const clampTimeDigit = (
  mode: 'hour' | 'minute',
  raw: string,
): string => {
  if (raw.trim() === '') {
    return ''
  }

  const numeric = Number.parseInt(raw, 10)
  if (Number.isNaN(numeric)) {
    return ''
  }

  if (mode === 'hour') {
    if (numeric < 1) return '1'
    if (numeric > 12) return '12'
    return String(numeric)
  }

  if (numeric < 0) return '00'
  if (numeric > 59) return '59'
  return String(numeric)
}

export const normalizeMinuteOnBlur = (minute: string): string => {
  if (!minute) return ''
  return minute.padStart(2, '0')
}

export const dayTitlesSummary = (days: WorkingDayKey[]): string =>
  days.map((day) => WORKING_DAY_TITLES[day]).join(', ')
